import {ChangeEvent, useCallback, useContext, useMemo, useState} from 'react';
import {TestType} from 'quantum-graph';
import Modal from '../../../../components/modal';
import Fuse from 'fuse.js';
import './table-filter.scss';
import Variable, {ValueType} from '../../../../lib/metadata';
import Filter from '../../../../lib/filter';
import Select from '../../../../components/select';
import Autocomplete from '../../../../components/autocomplete';
import Button from '../../../../components/button';
import UploadLink from '../../../../components/variable-filter/upload-link';
import Badge from '../../../../components/badge';
import {HeaderContext} from '../../../../components/header/context';
import {TextInput} from '@carbon/react';

export type TestDataType = {
    value: string,
    label: string,
};

export const numberTests = [
    {value: TestType.Lt, label: 'Less Than'},
    {value: TestType.LtEq, label: 'Less Than or Equal'},
    {value: TestType.Eq, label: 'Equals'},
    {value: TestType.NEq, label: 'Not Equals'},
    {value: TestType.GtEq, label: 'Greater Than or Equal'},
    {value: TestType.Gt, label: 'Greater Than'},
    {value: TestType.In, label: 'Is One Of'},
    {value: TestType.NotIn, label: 'Is Not One Of'},
    {value: TestType.Null, label: 'Not Has Data'},
    {value: TestType.NotNull, label: 'Has Data'},
];

export const textTests = [
    {value: TestType.Eq, label: 'Equals'},
    {value: TestType.NEq, label: 'Not Equals'},
    {value: TestType.Like, label: 'Text Includes'},
    {value: TestType.In, label: 'Is One Of'},
    {value: TestType.NotIn, label: 'Is Not One Of'},
    {value: TestType.Null, label: 'Not Has Data'},
    {value: TestType.NotNull, label: 'Has Data'},
];

export const uploadTests: Array<string> = [
    TestType.Eq,
    TestType.NEq,
    TestType.In,
    TestType.NotIn,
];

type Props = {
    config: { variable: Variable, filter: Filter | null },
    data: Array<Record<string, any>>;
    displayPattern?: string;
    placeholder?: string;
    onApply: (f: Filter) => void,
    onCancel: () => void,
    minSearchChars?: number;
};

const MIN_INPUT_LENGTH = 3;

function formatOptionLabel(pattern: string, row: Record<string, any>) {
    return pattern.replace(/\{(\w+)}/g, (_match, p1) => row[p1] as string);
}

const TableFilter = (props: Props) => {
    const {
        config,
        data,
        displayPattern,
        placeholder = 'Search...',
        onApply,
        onCancel,
        minSearchChars = MIN_INPUT_LENGTH,
    } = props;
    const variable = config.variable;
    const searchField = variable.name;
    const [test, setTest] = useState<string>(TestType.Eq);
    const isNumber = variable.getValueType() === ValueType.Number || variable.getValueType() === ValueType.DateTime;
    const isIn = test === TestType.In || test === TestType.NotIn;
    const isLike = test === TestType.Like;
    const isNull = test === TestType.Null || test === TestType.NotNull;
    const tests = isNumber ? numberTests : textTests;
    const [value, setValue] = useState<string>('');
    const [values, setValues] = useState<Array<string>>([]);
    const inputValid = useMemo(() => {
        if (variable.getValueType() === ValueType.DateTime && test !== TestType.Null && test !== TestType.NotNull) {
            const dateRegEx = /^\d{4}-\d{2}-\d{2}$/;
            return dateRegEx.test(value);
        }

        return isNumber ? !isNaN(Number(value)) : true;
    }, [value, test]);
    const canUpload = uploadTests.includes(test);
    const showAuto = !isNull && variable.getValueType() === ValueType.Text && !isLike;
    const showInput = isLike || (!isNull && !showAuto);
    const {useNewDesign} = useContext(HeaderContext);


    // Fuse.js instance for fuzzy searching
    const fuse = new Fuse(data, {
        threshold: 0.3, // Adjust the threshold for fuzzy matching
        keys: [variable.name],
    });

    function add() {
        values.push(value);
        setValues(values);
        setValue('');
    }

    function addMultiple(items: Array<string>) {
        if (isNumber)
            items = items.filter(v => !isNaN(Number(v)));
        setValues(v => v.concat(items));
        if (test === TestType.Eq)
            setTest(TestType.In);
        else if (test === TestType.NEq)
            setTest(TestType.NotIn);
    }

    function remove(i: number) {
        const newValues = values.slice();
        newValues.splice(i, 1);
        setValues(newValues);
    }

    function applyFilter() {
        let filterValue: string | boolean = checkPercentage(value);
        if (variable.type === 'bit')
            filterValue = filterValue === 'true';
        const filter: Filter = {
            variable,
            test: test as TestType,
            value: isIn ? values.map(v => checkPercentage(v)) : filterValue,
        };
        if (test === TestType.Like)
            filter.value = '%' + value + '%';
        onApply(filter);
    }

    function checkPercentage(value: string): string {
        const format = variable.getTag('Format')?.toLowerCase();
        if (format === 'percent' || format === 'percentfloor' || format === 'bigpercent') {
            const numValue = Number(value);
            // If user enters a percentage value (e.g., 5 for 5%), convert to decimal (0.05)
            // Values >= 1 are treated as percentages, values < 1 are already decimals
            if (!isNaN(numValue) && numValue >= 1) {
                return (numValue / 100).toString();
            }
        }
        return value;
    }

    // Function to get options based on the input
    const getOptions = useCallback(async (input: string): Promise<Array<string>> => {
        if (!input || input.length < minSearchChars) {
            // Return all unique options if no input
            return Array.from(
                new Set(
                    data
                        .map(row => displayPattern ? formatOptionLabel(displayPattern, row) : row[searchField])
                        .filter(v => v !== null && v !== undefined && v !== '')
                )
            );
        }
        const results = fuse.search(input).map((result) => result.item);

        return Array.from(
            new Set(
                results.map(row =>
                    displayPattern ? formatOptionLabel(displayPattern, row) : row[searchField])
                    .filter(v => v !== null && v !== undefined && v !== '')
            ));
    }, [fuse, displayPattern, searchField, minSearchChars, data]);

    const getPlaceholder = () => {
        if (variable.getValueType() === ValueType.DateTime) return 'YYYY-MM-DD';
        const format = variable.getTag('Format')?.toLowerCase();
        if (format === 'percent' || format === 'percentfloor' || format === 'bigpercent') return 'Enter percentage (e.g., 5 for 5%)';
        if (isNumber) return 'Enter number';
        return 'Search...';
    };

    const inputProps = {id: 'filter-input', labelText: '', type: 'text', className: inputValid ? '' : 'invalid', value, onChange: (e: ChangeEvent<HTMLInputElement>) => setValue(e.target.value), placeholder: getPlaceholder(), ['data-testid']: 'filter-input'};
    
    return <Modal title="Filter" onClose={onCancel} className="!p-0 flex flex-col" headerClassName="scrollable-modal-header" primaryButtonText="Save" secondaryButtonText="Cancel" primaryButtonOnClick={applyFilter} secondaryButtonOnClick={onCancel} primaryButtonDisabled={!inputValid || (isIn ? values.length === 0 : (!isNull ? !value : false))}>
        <div className="p-4 pb-3">
            <div>
                <strong>{variable.label}</strong>
            </div>
            <div className="mt-2">
                <Select<TestDataType> data={tests} value={test} onChange={setTest} data-testid='filter-type-dropdown'>
                    {d => d.value}
                    {d => d.label}
                </Select>
            </div>
            <div className="mt-2 grid">
                {data && showAuto && <Autocomplete
                    value={value}
                    onChange={setValue}
                    getOptions={getOptions}
                    placeholder={placeholder}
                    getOptionsOnLoad
                    icon={value ? 'icon-down' : 'icon-search'}
                    data-testid="filter-value-autocomplete-dropdown"
                    allowFreeform
                />}
                {showInput && (useNewDesign ? <TextInput {...inputProps} /> : <input {...inputProps} />)}
                {isIn && <Button color="blue" onClick={add} className="h-10 mt-2" disabled={!value || !inputValid} data-testid="multi-add-button"><i
                    className="icon-plus"/> Add</Button>}
                {!inputValid && <div className="text-sm text-orange-500">Invalid value</div>}
            </div>
            {canUpload && <div className="mt-2">
                <UploadLink onChange={addMultiple}/>
            </div>}
            {isIn && <div>
                <hr/>
                {!values.length && <em className="text-gray-500" data-testid="no-values-added-tag">No values added</em>}
                {values.map((v, i) => <Badge color="light" key={i} pill onClose={() => remove(i)}>{v}</Badge>)}
            </div>}
        </div>
    </Modal>;
};

export default TableFilter;
